/**
 * 첨부 차단 확장자 클라이언트 스토어
 *
 * 실제로 차단되는 확장자는 두 갈래다(server/services/blockedExtensions.js 참고):
 *  - managed: 관리자 콘솔에서 추가·해제하는 목록
 *  - critical: 코드에 박힌 절대 차단 목록 (관리자도 해제 불가)
 * 두 목록을 클라이언트가 알아야 (1) 파일 선택·드롭 즉시 사전 차단하고 (2) 도움말이
 * 실제 정책과 어긋나지 않는다. 예전에는 도움말이 목록을 하드코딩해 두어 관리자가 목록을
 * 바꿔도 안내가 그대로였고, 절대 차단 목록은 어느 화면에도 없었다.
 *
 * 두 경로로 갱신된다:
 *  - pull: 부팅 + 소켓 재연결 시 GET /api/config/blocked-extensions (인증 불필요)
 *  - push: 관리자가 바꾸면 소켓 'config:blocked-extensions' (socketService.ts → App.tsx)
 *
 * Context가 아니라 모듈 레벨 스토어인 이유는 uploadPolicyService와 같다 — ChatWindow의
 * addFiles가 deps 없이 동기 게터로 읽어야 Tauri 드롭 리스너가 재등록되지 않는다.
 * 반응성이 필요한 화면(도움말·관리자 콘솔)은 useBlockedExtensions() 훅을 쓴다.
 */
import { useEffect, useSyncExternalStore } from 'react';
import { apiFetch } from './apiClient';

export interface BlockedExtensionLists {
  /** 관리자 콘솔에서 해제할 수 있는 목록 */
  managed: string[];
  /** 코드 상수 — 관리자도 해제 불가 */
  critical: string[];
  /** 서버 값을 한 번이라도 받았는지 (도움말의 "불러오는 중" 판단용) */
  loaded: boolean;
}

// 초기값은 빈 목록 = fail-open. 최종 판정은 서버 multer가 하므로 못 받았거나 stale해도
// 보안 문제는 없고, 사전 차단만 잠깐 쉬어간다.
const EMPTY: BlockedExtensionLists = { managed: [], critical: [], loaded: false };

let current: BlockedExtensionLists = EMPTY;
let lookup: Set<string> = new Set();
let inFlight: Promise<void> | null = null;
const listeners = new Set<() => void>();

function normalize(list: unknown): string[] {
  if (!Array.isArray(list)) return [];
  return Array.from(
    new Set(
      list
        .map((item: any) => (typeof item === 'string' ? item : item?.extension))
        .filter((ext: unknown): ext is string => typeof ext === 'string' && ext.length > 1)
        .map((ext: string) => ext.toLowerCase())
    )
  ).sort();
}

function same(a: string[], b: string[]): boolean {
  return a.length === b.length && a.every((v, i) => v === b[i]);
}

/** 동기 getter — addFiles가 의존성 없이 읽는다 */
export function getBlockedExtensions(): BlockedExtensionLists {
  return current;
}

/**
 * 파일명에서 확장자를 뗀다. 서버 path.extname과 같은 규칙 —
 * 점이 없거나 '.bashrc'처럼 점으로 시작만 하면 확장자 없음('').
 */
export function extensionOf(fileName: string): string {
  if (typeof fileName !== 'string') return '';
  const dot = fileName.lastIndexOf('.');
  if (dot <= 0) return '';
  return fileName.slice(dot).toLowerCase();
}

/** 파일명 하나가 차단 대상인지. 목록을 못 받았으면 항상 false = fail-open(서버가 최종 판정) */
export function isBlockedFileName(fileName: string): boolean {
  const ext = extensionOf(fileName);
  return ext !== '' && lookup.has(ext);
}

/**
 * 스토어 갱신. 세 가지 payload를 모두 받는다:
 *  - { managed, critical }  (GET /api/config/blocked-extensions)
 *  - { extensions: [...] }  (소켓 config:blocked-extensions — 관리 목록만 바뀐다)
 *  - string[]               (방어적)
 */
export function setBlockedExtensions(next: any): void {
  if (!next) return;
  const hasManaged = Array.isArray(next) || Array.isArray(next.managed) || Array.isArray(next.extensions);
  const managed = hasManaged
    ? normalize(Array.isArray(next) ? next : next.managed ?? next.extensions)
    : current.managed;
  const hasCritical = Array.isArray(next.critical);
  const critical = hasCritical ? normalize(next.critical) : current.critical;
  // 소켓 push는 관리 목록만 싣는다 — 그걸로 loaded를 켜면 절대 차단 목록을 영영 못 받는다
  // (도움말이 '항상 차단' 섹션 없이 굳는다). loaded는 pull 응답에서만 켠다.
  const loaded = current.loaded || hasCritical;

  // ⚠ 값이 같으면 참조를 그대로 둔다. 매번 새 객체를 만들면 useSyncExternalStore가 무한 렌더한다.
  if (loaded === current.loaded && same(managed, current.managed) && same(critical, current.critical)) return;

  current = { managed, critical, loaded };
  lookup = new Set([...managed, ...critical]);
  listeners.forEach(listener => listener());
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** 부팅·재연결 시 pull. 실패해도 빈 목록으로 계속 동작한다(최종 판정은 서버). */
export function fetchBlockedExtensions(): Promise<void> {
  if (inFlight) return inFlight;
  inFlight = apiFetch<{ managed: string[]; critical: string[] }>('/config/blocked-extensions')
    .then(setBlockedExtensions)
    .catch(err => {
      console.warn('[BlockedExt] 차단 확장자 목록 조회 실패, 서버 검증에 맡깁니다:', err);
    })
    .finally(() => {
      inFlight = null;
    });
  return inFlight;
}

/**
 * 값 변화에 반응해야 하는 화면(도움말·관리자 콘솔)용.
 * 도움말은 로그인 흐름 밖(/help)에서도 열리므로 아직 못 받았으면 여기서 1회 당겨온다.
 */
export function useBlockedExtensions(): BlockedExtensionLists {
  const value = useSyncExternalStore(subscribe, getBlockedExtensions, getBlockedExtensions);
  useEffect(() => {
    if (!value.loaded) fetchBlockedExtensions();
  }, [value.loaded]);
  return value;
}
